import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  StyledDialog,
  DialogHeaderStyled,
  StyledCloseButton,
  UserListContainer,
  UserListItem,
  DeleteButton,
  AddUserButton,
  StyledFormControl,
  StyledSelect,
} from '../../../utils/common/styles/commonStyles';
import Box from '@mui/material/Box';
import { Close, Person, PersonOutline, WorkOutline, Delete } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import {
  companyUsers,
  collectionUsers,
  addUserCollectionLoading,
  addUserCollectionError,
  companyUsersLoaded,
  collectionUsersLoaded,
} from '../../user/store/user.selectors';
import {
  fetchCompanyUsers,
  fetchCollectionUsers,
  assignCollectionToUser,
  deleteUserFromCollection,
  resetAlert,
} from '../../user/store/user.actions';
import TableSkeleton from '../../../utils/common/components/TableSkeleton';

const CollectionUsers = ({ isUsersListOpen, onClose, selectedCollection }) => {
  const dispatch = useDispatch();

  const compUsers = useSelector(companyUsers);
  const jbUsers = useSelector(collectionUsers);
  const compUsersLoaded = useSelector(companyUsersLoaded);
  const collUsersLoaded = useSelector(collectionUsersLoaded);
  const addJbUsrError = useSelector(addUserCollectionError);
  const addJbUsrLoading = useSelector(addUserCollectionLoading);

  const [selectedUser, setSelectedUser] = useState(null);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [removeAlert, setRemoveAlert] = useState(false);
  const [removeErrorAlert, setRemoveErrorAlert] = useState(false);
  const [filteredList, setFilteredList] = useState([]);

  useEffect(() => {
    if (compUsersLoaded && collUsersLoaded) {
      if (jbUsers && jbUsers.length) {
        setFilteredList(compUsers?.filter((u) => !jbUsers?.some((s) => s.userId === u.id)) || []);
      } else {
        setFilteredList(compUsers || []);
      }
    }
  }, [compUsers, jbUsers, compUsersLoaded, collUsersLoaded]);

  useEffect(() => {
    if (isUsersListOpen && selectedCollection) {
      dispatch(fetchCompanyUsers(selectedCollection.companyID));
      dispatch(fetchCollectionUsers(selectedCollection.id || selectedCollection.collectionId));
    }
  }, [isUsersListOpen, selectedCollection, dispatch]);

  const handleUsersClose = (e, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    setSelectedUser(null);
    setSuccessAlert(false);
    setErrorAlert(false);
    setRemoveAlert(false);
    setRemoveErrorAlert(false);
    dispatch(resetAlert());
    onClose();
  };

  const handleSelectUser = (e) => {
    setSelectedUser(e.target.value);
  };

  const handleAddUserToCollection = async () => {
    if (!selectedUser || !selectedCollection) return;

    const data = {
      collectionID: selectedCollection.id || selectedCollection.collectionId,
      userID: selectedUser.id,
    };
    await dispatch(assignCollectionToUser(data));
    if (!addJbUsrError && !addJbUsrLoading) setSuccessAlert(true);
    if (addJbUsrError) setErrorAlert(true);
    dispatch(fetchCollectionUsers(selectedCollection.id || selectedCollection.collectionId));
    setSelectedUser(null);
  };

  const handleRemoveUser = async (user) => {
    const data = { collectionID: user.collectionId, userID: user.userId };
    const response = await dispatch(deleteUserFromCollection(data));
    if (response?.status === 'success') {
      setRemoveAlert(true);
      await dispatch(
        fetchCollectionUsers(selectedCollection.id || selectedCollection.collectionId)
      );
      setTimeout(() => setRemoveAlert(false), 3000);
    } else {
      setRemoveErrorAlert(true);
      setTimeout(() => setRemoveErrorAlert(false), 3000);
    }
  };
  return (
    <StyledDialog
      open={isUsersListOpen}
      onClose={handleUsersClose}
      maxWidth="md"
      slotProps={{ paper: { sx: { minWidth: '550px' } } }}
    >
      <DialogHeaderStyled>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" gap={1}>
            <Person />
            User Management
          </Box>
          <StyledCloseButton onClick={handleUsersClose} size="small">
            <Close />
          </StyledCloseButton>
        </Box>
      </DialogHeaderStyled>

      <DialogContent sx={{ padding: 3 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: '#2c3e50',
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WorkOutline color="action" />
          Collection Users
        </Typography>

        {!collUsersLoaded ? (
          <TableSkeleton />
        ) : jbUsers && jbUsers.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              backgroundColor: 'rgba(0, 176, 155, 0.1)',
              border: '1px solid rgba(0, 176, 155, 0.3)',
              '& .MuiAlert-icon': { color: '#00B09B' },
              marginBottom: 3,
            }}
          >
            No users assigned to this collection yet
          </Alert>
        ) : (
          <UserListContainer>
            <List sx={{ padding: 1 }}>
              {jbUsers?.map((user) => (
                <UserListItem
                  key={user.userId}
                  disablePadding
                  secondaryAction={
                    <DeleteButton edge="end" size="small" onClick={() => handleRemoveUser(user)}>
                      <Delete fontSize="small" />
                    </DeleteButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#2c3e50',
                        }}
                      >
                        {user.userName}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontSize: '0.775rem',
                          color: '#64748b',
                          fontWeight: 500,
                        }}
                      >
                        {user.companyName}
                      </Typography>
                    }
                  />
                </UserListItem>
              ))}
            </List>
          </UserListContainer>
        )}

        <Divider sx={{ my: 2, opacity: 0.5 }} />

        <Box mt={2}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: '#2c3e50',
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PersonOutline color="action" />
            Assign New User
          </Typography>

          <StyledFormControl required fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select a user</InputLabel>
            <StyledSelect
              name="addedUserId"
              value={selectedUser || ''}
              onChange={handleSelectUser}
              label="Select a user"
              size="small"
            >
              {filteredList?.map((user) => (
                <MenuItem value={user} key={user.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonOutline fontSize="small" color="action" />
                    {user.name}
                  </Box>
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>

          {selectedUser && (
            <AddUserButton variant="contained" onClick={handleAddUserToCollection} size="small">
              Add User to Collection
            </AddUserButton>
          )}

          {successAlert && !addJbUsrError && (
            <Alert
              severity="success"
              timeout={3000}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(0, 176, 155, 0.1)',
                border: '1px solid rgba(0, 176, 155, 0.3)',
                '& .MuiAlert-icon': { color: '#00B09B' },
                marginTop: 2,
              }}
            >
              User successfully assigned to the collection!
            </Alert>
          )}
          {errorAlert && addJbUsrError && (
            <Alert
              severity="error"
              timeout={3000}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(0, 176, 155, 0.1)',
                border: '1px solid rgba(0, 176, 155, 0.3)',
                '& .MuiAlert-icon': { color: '#b00000' },
                marginTop: 2,
              }}
            >
              User Can&apos;t be assigned to the collection. Please try again.
            </Alert>
          )}
          {removeAlert && (
            <Alert
              severity="success"
              timeout={3000}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(0, 176, 155, 0.1)',
                border: '1px solid rgba(0, 176, 155, 0.3)',
                '& .MuiAlert-icon': { color: '#00B09B' },
                marginTop: 2,
              }}
            >
              User successfully removed from the collection!
            </Alert>
          )}
          {removeErrorAlert && (
            <Alert
              severity="error"
              timeout={3000}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(0, 176, 155, 0.1)',
                border: '1px solid rgba(0, 176, 155, 0.3)',
                '& .MuiAlert-icon': { color: '#b00000' },
                marginTop: 2,
              }}
            >
              User couldn&apos;t be removed from the collection. Please try again.
            </Alert>
          )}
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default CollectionUsers;

CollectionUsers.propTypes = {
  isUsersListOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCollection: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    collectionName: PropTypes.string,
  }),
};
